import { parseJsonIfValid } from '@salescore/frontend-common'
import { Result } from 'effect'
import { t } from 'i18next'

import { validateAndNormalize } from '../../rsheet/action/validateAndNormalize'
import type { RecordChange } from '../../rsheet/util/updateViewRecords'
import type { RSheetColumn, RSheetRecordNode, RSheetsColumnMeta } from '../column'
import { isPlaceholderCellValue } from '../copyMatrix'
import type { RelationPaste } from './relationPaste'

export const generateUpdateChange = ({
  column,
  value,
  relationPaste,
  rowIndex,
  innerRowIndex,
}: {
  column: RSheetColumn<RSheetRecordNode>
  value: unknown
  relationPaste: RelationPaste | undefined
  rowIndex: number
  innerRowIndex: number
}): Result.Result<
  RecordChange & { metaType?: RSheetsColumnMeta; relationPaste: RelationPaste | undefined },
  { message: string } | undefined
> => {
  if (column.field === undefined) {
    // cursor / expand が system column（rowIndex / rowCheckbox / open）を指している状態で paste が走ると
    // column.field.nodePath で crash するため、system column は silent skip する
    return Result.fail(undefined)
  }
  if (column.readonly === true) {
    return Result.fail({
      message: t(`このカラムは書き込み禁止です`),
    })
  }
  const copiedValueFromClipboard = convertStringValueToCopiedValue(value)
  const copiedValue = isPlaceholderCellValue(copiedValueFromClipboard) ? null : copiedValueFromClipboard
  if (copiedValue === null) {
    return Result.succeed({
      rowIndex,
      innerRowIndex,
      nodePath: column.field.nodePath,
      isNestedColumn: column.node.path.length > 1,
      field: column.field,
      metaType: column.metaType,
      value: null,
      label: undefined,
      relationPaste,
    })
  }
  const normalizeResult = validateAndNormalize(copiedValue, column)
  if (Result.isFailure(normalizeResult)) {
    return Result.fail(normalizeResult.failure)
  }
  const normalizedValue = normalizeResult.success
  if (column.metaType === 'relation' && normalizedValue === '') {
    // 参照列の場合、空文字を recordChanges に push すると一番上のレコードが選択されてしまうため何もしない
    return Result.fail(undefined)
  }
  return Result.succeed({
    rowIndex,
    innerRowIndex,
    nodePath: column.field.nodePath,
    isNestedColumn: column.node.path.length > 1,
    field: column.field,
    metaType: column.metaType,
    value: normalizedValue,
    label: undefined,
    relationPaste,
  })
}

function convertStringValueToCopiedValue(x: unknown): unknown {
  if (typeof x === 'string' && x.startsWith('"') && x.endsWith('"')) {
    return parseJsonIfValid(x)
  }
  return x
}
